import { useState, useCallback } from 'react';
import {
  DndContext,
  DragOverlay,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useDroppable,
  useSensor,
  useSensors,
  type DragStartEvent,
  type DragOverEvent,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { isFlatProject, type FlatItem } from './types';
import type { LineItemData, ProjectData } from './LineItemList';
import { DragOverlayContent } from './LineItemList';
import { LineItem } from './LineItem';
import { ProjectZone } from './ProjectZone';
import { ProjectDragOverlay } from './ProjectDragOverlay';
import type { SubItemForm } from './subItemTypes';

function DropIndicatorLine() {
  return (
    <div className="h-0.5 bg-blue-500 rounded-full mx-1 my-0.5 shadow-[0_0_4px_rgba(59,130,246,0.5)]" />
  );
}

// Sentinel drop target rendered after the last item so a line item can be
// dropped at the very END of the list. Without it, the insert-BEFORE drop
// semantics leave the final position unreachable. Only rendered when a
// consumer wires `onMoveToEnd`.
const END_DROP_ZONE_ID = '__breakdown_end_drop_zone__';

// Sentinel id for the "drop a loose item just before this project" strips.
// Encodes the projectId so the drag-end handler can route the drop through the
// normal onDragEnd path (which inserts loose-before-project when the over
// target is a project header).
const BEFORE_PROJECT_PREFIX = '__before_project__:';

function EndDropZone({ isActive }: { isActive: boolean }) {
  const { setNodeRef, isOver } = useDroppable({ id: END_DROP_ZONE_ID });
  return (
    <div ref={setNodeRef} data-testid="breakdown-end-drop-zone">
      {isActive && isOver && <DropIndicatorLine />}
      {/* Only claim drop space while a line item is being dragged; collapse
          to nothing otherwise so the list has no dead space below the last row. */}
      <div className={isActive ? 'h-10' : 'h-px'} />
    </div>
  );
}

// Drop strip rendered above a project so a loose item can be dropped into the
// space right before the section. Like EndDropZone, it only claims space while
// a line item is being dragged, so sections aren't pushed apart at rest.
function BeforeProjectDropZone({ projectId, isActive }: { projectId: string; isActive: boolean }) {
  const { setNodeRef, isOver } = useDroppable({ id: `${BEFORE_PROJECT_PREFIX}${projectId}` });
  return (
    <div ref={setNodeRef} data-testid={`breakdown-before-project-${projectId}`}>
      {isActive && isOver && <DropIndicatorLine />}
      <div className={isActive ? 'h-6' : 'h-px'} />
    </div>
  );
}

interface BreakdownDndWrapperProps {
  flatItems: FlatItem[];
  visibleSortableIds: string[];
  collapsedProjects: Set<string>;
  selectedItemId: string | null;
  expandedItemId: string | null;
  autoFocusItemId?: string | null;
  onAutoFocusComplete?: () => void;
  onSelectItem: (id: string | null) => void;
  onDeleteLineItem: (id: string) => void;
  onCategoryChange: (id: string, category: string) => void;
  onDescriptionChange: (id: string, description: string) => void;
  onItemDateChange?: (id: string, itemDate: string) => void;
  onDragEnd: (activeId: string, overItem: FlatItem, isCollapsedProject: boolean) => void;
  onProjectDragStart: (projectId: string) => void;
  onProjectDragEnd: (activeId: string, overItem: FlatItem) => void;
  /** When provided, renders an end drop-zone so a line item can be dropped at the very end of the list. */
  onMoveToEnd?: (activeId: string) => void;
  /** When true, renders "before project" drop strips so a loose item can be dropped into the space above a project section. */
  enableSectionDropZones?: boolean;
  onProjectDragCancel: () => void;
  onToggleCollapse: (projectId: string) => void;
  onRenameProject: (projectId: string, name: string) => void;
  onDeleteProject: (projectId: string) => void;
  onAddLineItemToProject: (projectId: string) => void;
  readOnly?: boolean;
  renderAccordionBody?: (item: LineItemData) => React.ReactNode;
  checkedIds?: Set<string>;
  onCheckItem?: (id: string, checked: boolean) => void;
  onCheckProject?: (projectId: string, checked: boolean) => void;
  getProjectCheckState?: (projectId: string) => { isChecked: boolean; isIndeterminate: boolean };
  /** Compute line item total from sub-items using current rates & categories. Avoids stale cached totalCost. */
  computeItemTotal?: (subItems: SubItemForm[]) => number;
  useRingHighlight?: boolean;
}

export function BreakdownDndWrapper({
  flatItems,
  visibleSortableIds,
  collapsedProjects,
  selectedItemId,
  expandedItemId,
  autoFocusItemId,
  onAutoFocusComplete,
  onSelectItem,
  onDeleteLineItem,
  onCategoryChange,
  onDescriptionChange,
  onItemDateChange,
  onDragEnd,
  onProjectDragStart,
  onProjectDragEnd,
  onMoveToEnd,
  enableSectionDropZones = false,
  onProjectDragCancel,
  onToggleCollapse,
  onRenameProject,
  onDeleteProject,
  onAddLineItemToProject,
  readOnly = false,
  renderAccordionBody,
  checkedIds,
  onCheckItem,
  onCheckProject,
  getProjectCheckState,
  computeItemTotal,
  useRingHighlight,
}: BreakdownDndWrapperProps) {
  const [dragActiveId, setDragActiveId] = useState<string | null>(null);
  const [dragActiveType, setDragActiveType] = useState<'project-header' | 'line-item' | null>(null);
  const [overId, setOverId] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragStart = useCallback((event: DragStartEvent) => {
    if (readOnly) return;
    const activeId = event.active.id as string;
    const activeItem = flatItems.find((item) => item.id === activeId);
    if (!activeItem) return;

    setDragActiveId(activeId);
    setDragActiveType(activeItem.type);

    // Collapse any expanded item when drag starts
    if (expandedItemId) {
      onSelectItem(null);
    }

    if (activeItem.type === 'project-header') {
      onProjectDragStart(activeId);
    }
    // Note: do NOT call onSelectItem(activeId) for line items during drag —
    // that would re-expand the item we just collapsed. Selection happens on click, not drag.
  }, [readOnly, flatItems, onProjectDragStart, onSelectItem, expandedItemId]);

  const handleDragOver = useCallback((event: DragOverEvent) => {
    setOverId(event.over ? (event.over.id as string) : null);
  }, []);

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    if (readOnly) return;
    const activeType = dragActiveType;
    const activeId = event.active.id as string;

    setDragActiveId(null);
    setDragActiveType(null);
    setOverId(null);

    const { over } = event;
    if (!over) return;

    // Dropped on the end sentinel → append a line item to the very end.
    if (over.id === END_DROP_ZONE_ID) {
      if (activeType === 'line-item') onMoveToEnd?.(activeId);
      return;
    }

    // Dropped on a "before project" strip → insert the loose item just above
    // that project. Route through the normal onDragEnd path using the project
    // header as the over target (which yields loose-before-project).
    if (typeof over.id === 'string' && over.id.startsWith(BEFORE_PROJECT_PREFIX)) {
      if (activeType === 'line-item') {
        const projectId = over.id.slice(BEFORE_PROJECT_PREFIX.length);
        const headerItem = flatItems.find((item) => item.id === projectId);
        if (headerItem) onDragEnd(activeId, headerItem, false);
      }
      return;
    }

    const overItem = flatItems.find((item) => item.id === (over.id as string));
    if (!overItem) return;

    if (activeType === 'project-header') {
      onProjectDragEnd(activeId, overItem);
    } else {
      const isCollapsed = overItem.type === 'project-header' && collapsedProjects.has(overItem.id);
      onDragEnd(activeId, overItem, isCollapsed);
    }
  }, [readOnly, dragActiveType, flatItems, onProjectDragEnd, onDragEnd, onMoveToEnd]);

  const handleDragCancel = useCallback(() => {
    const activeType = dragActiveType;
    setDragActiveId(null);
    setDragActiveType(null);
    setOverId(null);

    if (activeType === 'project-header') {
      onProjectDragCancel();
    }
  }, [dragActiveType, onProjectDragCancel]);

  // Build the drag overlay content
  const dragOverlayNode = (() => {
    if (!dragActiveId) return null;
    const activeItem = flatItems.find((item) => item.id === dragActiveId);
    if (!activeItem) return null;

    if (dragActiveType === 'project-header') {
      const projectData = activeItem.data as ProjectData;
      const subtotal = projectData.lineItems.reduce((sum, li) => sum + (computeItemTotal ? computeItemTotal(li.subItems) : li.totalCost), 0);
      return (
        <ProjectDragOverlay
          name={projectData.name}
          itemCount={projectData.lineItems.length}
          subtotal={subtotal}
        />
      );
    }

    return <DragOverlayContent item={activeItem.data as LineItemData} />;
  })();

  // Check if an item is the current drop target (for blue line indicator)
  const isOverItem = (id: string) => dragActiveId !== null && overId === id && overId !== dragActiveId;

  // Check if a project zone is the drop target (for blue highlight)
  // Only highlights when the item will actually join the project:
  // - Collapsed header: drop goes into the project
  // - Expanded header: drop goes ABOVE as loose (no highlight)
  // - Member item: drop goes into the project
  const isProjectDropTarget = (projectId: string, memberItems: FlatItem[], isCollapsed: boolean) => {
    if (dragActiveId === null || dragActiveType !== 'line-item') return false;
    if (overId === projectId) return isCollapsed;
    return memberItems.some((m) => m.id === overId && overId !== dragActiveId);
  };

  // Render loop: group consecutive same-project items into ProjectZone components
  function renderItems() {
    const elements: React.ReactNode[] = [];
    let i = 0;
    let lineItemNumber = 1;

    while (i < flatItems.length) {
      const item = flatItems[i]!;

      if (isFlatProject(item)) {
        const projectId = item.id;
        const projectData = item.data as ProjectData;
        i++;
        const memberItems: FlatItem[] = [];
        while (i < flatItems.length && flatItems[i]!.projectId === projectId) {
          memberItems.push(flatItems[i]!);
          i++;
        }

        // Wrap the optional "before project" drop strip + the project zone in
        // a single element so the Panel's `space-y` treats the section as ONE
        // child — the strip lives inside and adds no extra inter-section gap.
        elements.push(
          <div key={projectId}>
            {enableSectionDropZones && (
              <BeforeProjectDropZone
                projectId={projectId}
                isActive={dragActiveType === 'line-item'}
              />
            )}
            {isOverItem(projectId) && !collapsedProjects.has(projectId) && (
              <DropIndicatorLine />
            )}
            <ProjectZone
            projectId={projectId}
            projectData={projectData}
            memberItems={memberItems}
            collapsed={collapsedProjects.has(projectId)}
            isDropTarget={isProjectDropTarget(projectId, memberItems, collapsedProjects.has(projectId))}
            selectedItemId={selectedItemId}
            expandedItemId={expandedItemId}
            autoFocusItemId={autoFocusItemId}
            onAutoFocusComplete={onAutoFocusComplete}
            onSelectItem={onSelectItem}
            onDeleteLineItem={onDeleteLineItem}
            onCategoryChange={onCategoryChange}
            onDescriptionChange={onDescriptionChange}
            onItemDateChange={onItemDateChange}
            onToggleCollapse={() => onToggleCollapse(projectId)}
            onRenameProject={(name) => onRenameProject(projectId, name)}
            onDeleteProject={() => onDeleteProject(projectId)}
            onAddLineItemToProject={() => onAddLineItemToProject(projectId)}
            readOnly={readOnly}
            overId={overId}
            dragActiveId={dragActiveId}
            startingItemNumber={lineItemNumber}
            renderAccordionBody={renderAccordionBody}
            checkedIds={checkedIds}
            onCheckItem={onCheckItem}
            onCheckProject={onCheckProject}
            projectCheckState={getProjectCheckState ? getProjectCheckState(item.id) : undefined}
            computeItemTotal={computeItemTotal}
            useRingHighlight={useRingHighlight}
          />
          </div>,
        );
        lineItemNumber += memberItems.length;
      } else {
        // Blue line before this loose item if it's the over target
        if (isOverItem(item.id)) {
          elements.push(<DropIndicatorLine key={`drop-${item.id}`} />);
        }

        const lineData = item.data as LineItemData;
        const isExpanded = expandedItemId === item.id;
        elements.push(
          <LineItem
            key={item.id}
            id={item.id}
            itemNumber={lineItemNumber}
            category={lineData.category}
            description={lineData.description}
            totalCost={computeItemTotal ? computeItemTotal(lineData.subItems) : lineData.totalCost}
            subItemCount={lineData.subItems.length}
            isSelected={selectedItemId === item.id}
            isExpanded={isExpanded}
            autoOpenCategory={autoFocusItemId === item.id}
            onAutoOpenComplete={onAutoFocusComplete}
            onClick={() => onSelectItem(item.id)}
            onDelete={() => onDeleteLineItem(item.id)}
            onCategoryChange={(category) => onCategoryChange(item.id, category)}
            onDescriptionChange={(description) => onDescriptionChange(item.id, description)}
            itemDate={lineData.itemDate}
            onItemDateChange={onItemDateChange ? (date) => onItemDateChange(item.id, date) : undefined}
            readOnly={readOnly}
            isChecked={checkedIds?.has(item.id)}
            onCheckedChange={onCheckItem ? (checked) => onCheckItem(item.id, checked) : undefined}
            useRingHighlight={useRingHighlight}
          >
            {isExpanded && renderAccordionBody?.(lineData)}
          </LineItem>
        );
        lineItemNumber++;
        i++;
      }
    }
    return elements;
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <SortableContext items={visibleSortableIds} strategy={verticalListSortingStrategy}>
        {renderItems()}
      </SortableContext>
      {onMoveToEnd && <EndDropZone isActive={dragActiveType === 'line-item'} />}
      <DragOverlay>
        {dragOverlayNode}
      </DragOverlay>
    </DndContext>
  );
}
